import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import DatePicker from 'react-datepicker';
import { createManualLead } from '../../../redux/actions/crmActions/CRMAction';
import { successNotification, errorNotification } from '../../../constants/Toast';
import './ManualLeadForm.scss';

const ManualLeadForm = ({ onLeadCreated }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    title: '',
    location: '',
    linkedInUrl: '',
    stage: 'POTENTIAL',
    dealSize: '',
    likelyHood: '',
    followUp: null,
    notes: '',
  });

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = date => {
    setFormData(prev => ({
      ...prev,
      followUp: date,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Validate required fields
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      errorNotification('First name and last name are required');
      return;
    }

    setLoading(true);

    try {
      await dispatch(createManualLead(formData));
      successNotification('Lead created successfully!');

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        companyName: '',
        title: '',
        location: '',
        linkedInUrl: '',
        stage: 'POTENTIAL',
        dealSize: '',
        likelyHood: '',
        followUp: null,
        notes: '',
      });

      // Callback to refresh CRM data
      if (onLeadCreated) {
        onLeadCreated();
      }
    } catch (error) {
      errorNotification(error.message || 'Failed to create lead');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      title: '',
      location: '',
      linkedInUrl: '',
      stage: 'POTENTIAL',
      dealSize: '',
      likelyHood: '',
      followUp: null,
      notes: '',
    });
  };

  return (
    <div className="manual-lead-form-container">
      <h3 className="form-title">Add New Lead Manually</h3>
      <p className="form-subtitle">Enter lead information to add them to your CRM</p>

      <form onSubmit={handleSubmit} className="manual-lead-form">
        <div className="form-section">
          <h4 className="section-title">Contact Information</h4>
          <div className="form-row">
            <div className="form-group required">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="common-input"
                placeholder="John"
                required
              />
            </div>
            <div className="form-group required">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="common-input"
                placeholder="Doe"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="common-input"
                placeholder="john.doe@example.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="common-input"
                placeholder="555-1234"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4 className="section-title">Company Information</h4>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="companyName">Company</label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                className="common-input"
                placeholder="Acme Corp"
              />
            </div>
            <div className="form-group">
              <label htmlFor="title">Job Title</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="common-input"
                placeholder="VP of Sales"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="common-input"
                placeholder="New York, NY"
              />
            </div>
            <div className="form-group">
              <label htmlFor="linkedInUrl">LinkedIn URL</label>
              <input
                type="url"
                id="linkedInUrl"
                name="linkedInUrl"
                value={formData.linkedInUrl}
                onChange={handleChange}
                className="common-input"
                placeholder="https://linkedin.com/in/johndoe"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4 className="section-title">Opportunity Details</h4>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="stage">Stage</label>
              <select
                id="stage"
                name="stage"
                value={formData.stage}
                onChange={handleChange}
                className="common-select"
              >
                <option value="POTENTIAL">Potential</option>
                <option value="INITIAL_CONTACT">Initial Contact</option>
                <option value="IN_CONVERSION">In Conversation</option>
                <option value="MEETING_BOOKED">Meeting Booked</option>
                <option value="FOLLOW_UP">Follow Up</option>
                <option value="CLOSED">Closed</option>
                <option value="LOST">Lost</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="likelyHood">Likelihood</label>
              <select
                id="likelyHood"
                name="likelyHood"
                value={formData.likelyHood}
                onChange={handleChange}
                className="common-select"
              >
                <option value="">Select</option>
                <option value="VERY_LIKELY">Very Likely</option>
                <option value="LIKELY">Likely</option>
                <option value="NOT_LIKELY">Not Likely</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dealSize">Deal Size ($)</label>
              <input
                type="number"
                id="dealSize"
                name="dealSize"
                value={formData.dealSize}
                onChange={handleChange}
                className="common-input"
                placeholder="50000"
                min="0"
              />
            </div>
            <div className="form-group">
              <label htmlFor="followUp">Follow Up Date</label>
              <DatePicker
                id="followUp"
                selected={formData.followUp}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                className="common-input"
                placeholderText="Select date"
                minDate={new Date()}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4 className="section-title">Notes</h4>
          <div className="form-group full-width">
            <label htmlFor="notes">Additional Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="common-textarea"
              placeholder="Add any additional notes about this lead..."
              rows="4"
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="button secondary-button"
            onClick={handleReset}
            disabled={loading}
          >
            Reset
          </button>
          <button type="submit" className="button primary-button" disabled={loading}>
            {loading ? 'Creating Lead...' : 'Create Lead'}
          </button>
        </div>
      </form>
    </div>
  );
};

ManualLeadForm.propTypes = {
  onLeadCreated: PropTypes.func,
};

export default ManualLeadForm;
